"use client"
import { type CSSProperties, useRef } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { useScroll } from "~/components/foundations/ScrollProvider"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { StatsStackedInterface } from "./StatsStacked.interface"
import s from "./StatsStacked.module.scss"

gsap.registerPlugin(ScrollTrigger)

export type StatsStackedViewProps = Omit<StatsStackedInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const StatsStackedView = ({
  layers,
  ctaText,
  config,
  moduleIndex,
  className,
  style,
}: StatsStackedViewProps) => {
  const configProps = getModuleConfigProps(config)
  const { lenis } = useScroll()
  const containerRef = useRef<HTMLElement>(null)
  const stepRefs = useRef<(HTMLDivElement | null)[]>([])

  // Build scroll steps: layer → (connector+divider?) → layer → ... → CTA
  // Each layer is a step; connectors between layers are also steps
  const steps: { type: "layer" | "connector" | "cta"; index: number }[] = []
  layers.forEach((layer, i) => {
    steps.push({ type: "layer", index: i })
    if (i < layers.length - 1 && layer.connectorText) {
      steps.push({ type: "connector", index: i })
    }
  })
  if (ctaText) {
    steps.push({ type: "cta", index: 0 })
  }

  const totalSteps = steps.length

  useGSAP(
    () => {
      if (!lenis) return

      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        if (!containerRef.current) return

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            scroller: lenis.rootElement,
            start: "top top",
            end: "bottom bottom",
            // Small numeric scrub damps per-frame scroll deltas from Lenis,
            // smoothing out the micro-jitter caused by `scrub: true`
            scrub: 0.5,
          },
          defaults: {
            ease: "linear",
          },
        })

        // Each step gets an equal slice of the timeline; fades take 30% of it
        const stepDuration = 1
        const fadeDuration = stepDuration * 0.3

        // Position (in timeline units) where a given step's slice begins
        const stepStart = (index: number) => index * stepDuration

        const fadeIn = (el: Element, position: number) =>
          tl.to(
            el,
            { autoAlpha: 1, y: 0, duration: fadeDuration, ease: "power2.out" },
            position,
          )

        const fadeOut = (
          el: Element,
          position: number,
          duration = fadeDuration,
        ) =>
          tl.to(
            el,
            { autoAlpha: 0, y: -20, duration, ease: "power2.in" },
            position,
          )

        // Hold a step in place to reserve scroll distance without animating
        const hold = (el: Element, position: number, duration: number) =>
          tl.to(el, { autoAlpha: 1, duration }, position)

        stepRefs.current.forEach((stepEl, index) => {
          if (!stepEl) return

          // Promote each step to its own GPU layer so opacity/transform changes
          // composite instead of triggering full repaints of the (large) text.
          // willChange is reverted automatically on cleanup.
          gsap.set(stepEl, { willChange: "transform, opacity" })

          const start = stepStart(index)

          if (index === 0) {
            gsap.set(stepEl, { autoAlpha: 1, y: 0 })
            fadeOut(stepEl, start, stepDuration)
          } else if (index < totalSteps - 1) {
            gsap.set(stepEl, { autoAlpha: 0, y: 20 })
            fadeIn(stepEl, start)
            fadeOut(stepEl, start + stepDuration * 0.7)
          } else {
            // Last step: fade in, then hold for the rest of its slice so it
            // stays on screen for the same scroll duration as the other steps
            gsap.set(stepEl, { autoAlpha: 0, y: 20 })
            fadeIn(stepEl, start)
            hold(stepEl, start + fadeDuration, stepDuration - fadeDuration)
          }
        })
      })
    },
    { scope: containerRef, dependencies: [lenis] },
  )

  let stepIndex = 0

  return (
    <section
      ref={containerRef}
      className={cn(s.statsStacked, `stack-module-${moduleIndex}`, className)}
      data-module="stats-stacked"
      aria-label="Brand Statistics"
      {...configProps}
      style={
        {
          ...configProps.style,
          ...style,
          "--total-steps": totalSteps,
        } as CSSProperties
      }
    >
      <div className={s.stickyContainer}>
        {layers.map((layer, layerIndex) => {
          const currentStepIndex = stepIndex++
          return (
            <div key={`layer-${layerIndex}`}>
              {/* Layer content */}
              <div
                ref={(el) => {
                  stepRefs.current[currentStepIndex] = el
                }}
                className={cn(s.layer, currentStepIndex === 0 && s.firstStep)}
                aria-live="polite"
              >
                {layer.statValue && (
                  <span className={s.statValue}>{layer.statValue}</span>
                )}
                {layer.description && (
                  <div className={s.description}>
                    <HtmlRenderer html={layer.description} />
                  </div>
                )}
              </div>

              {/* Connector + divider between layers */}
              {layerIndex < layers.length - 1 &&
                layer.connectorText &&
                (() => {
                  const connectorStepIndex = stepIndex++
                  return (
                    <div
                      ref={(el) => {
                        stepRefs.current[connectorStepIndex] = el
                      }}
                      className={s.connector}
                    >
                      {/* <span className={s.dividerLine} aria-hidden="true" /> */}
                      <span className={s.connectorText}>
                        {layer.connectorText}
                      </span>
                      {/* <span className={s.dividerLine} aria-hidden="true" /> */}
                    </div>
                  )
                })()}
            </div>
          )
        })}

        {/* CTA step */}
        {ctaText && (
          <div
            ref={(el) => {
              stepRefs.current[stepIndex] = el
            }}
            className={cn(s.layer, s.ctaLayer)}
          >
            <div className={s.ctaText}>
              <HtmlRenderer html={ctaText} />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
